import { useRef, useState } from "react";
import { ArrowUp, ImagePlus, Link2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const TYPES = ["image/png", "image/jpeg", "image/webp"];

// Shared prompt box (home + workspace). `upload(file)` returns an attachment entry; `onSend(text, images)` submits.
export const Composer = ({ onSend, upload, disabled, running, onStop, big = false, placeholder, children }) => {
  const [text, setText] = useState("");
  const [imgs, setImgs] = useState([]);
  const [linking, setLinking] = useState(false);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pick = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    for (const f of files) {
      if (imgs.length >= 5) { toast.error("Up to 5 images per message"); break; }
      if (!TYPES.includes(f.type)) { toast.error(`${f.name}: images must be PNG, JPG or WebP`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: images must be 10 MB or smaller`); continue; }
      setImgs((x) => [...x, { local: URL.createObjectURL(f), file: f, name: f.name }]);
    }
  };
  const addLink = () => {
    if (!/^https?:\/\/\S+$/i.test(link.trim())) return toast.error("Enter a full link starting with http:// or https://");
    setText((t) => `${t}${t && !t.endsWith(" ") ? " " : ""}${link.trim()} `); setLink(""); setLinking(false);
  };
  const send = async () => {
    if (!text.trim() || disabled || busy) return;
    setBusy(true);
    try {
      const entries = [];
      for (const i of imgs) entries.push(await upload(i.file));
      await onSend(text.trim(), entries);
      setText(""); setImgs([]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send"); }
    setBusy(false);
  };
  return (
    <div data-testid="composer" className={`rounded-2xl border border-white/10 bg-[#0B0F18] shadow-[0_0_0_1px_rgba(34,211,238,0.04)] focus-within:border-cyan-400/40 ${big ? "p-4" : "p-3"}`}>
      {imgs.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {imgs.map((i, k) => (
            <span key={i.local} className="relative">
              <img src={i.local} alt={i.name} data-testid={`composer-image-${k}`} className="h-14 w-14 rounded-lg border border-white/10 object-cover" />
              <button onClick={() => setImgs(imgs.filter((_, j) => j !== k))} aria-label="Remove image" data-testid={`composer-image-remove-${k}`} className="absolute -right-1.5 -top-1.5 rounded-full bg-black p-0.5 text-slate-300"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={big ? 4 : 2} placeholder={placeholder}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        data-testid="composer-input" className={`w-full resize-none bg-transparent text-white outline-none placeholder:text-slate-500 ${big ? "text-lg" : "text-sm"}`} />
      {linking && (
        <div className="mb-2 flex gap-2">
          <input autoFocus value={link} onChange={(e) => setLink(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLink()} placeholder="https://site-you-like.com" data-testid="composer-link-input"
            className="flex-1 rounded-lg border border-white/10 bg-black/30 px-3 py-1.5 text-sm text-white outline-none" />
          <button onClick={addLink} data-testid="composer-link-add" className="rounded-lg border border-cyan-400/40 px-3 text-xs text-cyan-200">Add</button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={pick} data-testid="composer-file-input" />
        <button onClick={() => fileRef.current.click()} aria-label="Attach image" title="Attach image" data-testid="composer-attach-image" className="rounded-full border border-white/10 p-2 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200"><ImagePlus className="h-4 w-4" /></button>
        <button onClick={() => setLinking(!linking)} aria-label="Add reference link" title="Reference link" data-testid="composer-attach-link" className="rounded-full border border-white/10 p-2 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200"><Link2 className="h-4 w-4" /></button>
        {children}
        <span className="flex-1" />
        {running ? (
          <button onClick={onStop} data-testid="composer-stop" className="inline-flex items-center gap-1.5 rounded-full border border-red-400/40 px-4 py-2 text-xs font-semibold text-red-200 hover:bg-red-500/10"><span className="h-2.5 w-2.5 rounded-sm bg-red-300" />Stop</button>
        ) : (
          <button onClick={send} disabled={!text.trim() || disabled || busy} aria-label="Send" data-testid="composer-send"
            className="grid h-9 w-9 place-items-center rounded-full bg-cyan-400 text-[#07090E] transition-colors hover:bg-cyan-300 disabled:bg-white/10 disabled:text-slate-500">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
          </button>
        )}
      </div>
    </div>
  );
};

export const uploadTo = (pid) => async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post(`/admin/studio/projects/${pid}/attachments`, fd)).data;
};
